import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Bot,
  Brain,
  Megaphone,
  Plus,
  Sparkles,
  Zap,
} from "lucide-react";
import HelpdeskSuiteLayout from "../../../layouts/helpdeskLayout/HelpdeskSuiteLayout";
import { PageHeader } from "../../../components/ui/PageHeader";
import { Card } from "../../../components/ui/Card";
import { Button } from "../../../components/ui/Button";
import { Switch } from "../../../components/ui/Switch";
import { Skeleton } from "../../../components/ui/Skeleton";
import { PendingBackendNotice } from "../../../components/ui/PendingBackendNotice";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/Tabs";
import { showToast } from "../../../utils/showToast";
import { apiErrorMessage, apiErrorStatus } from "../../../utils/apiErrorMessage";
import { useUrlState } from "../../../hooks/useUrlState";
import {
  deleteAiAgent,
  duplicateAiAgent,
  getAiAgents,
  getAiAgentsCatalog,
  isEndpointMissing,
  updateAiAgent,
} from "./ai/aiAgentsApi";
import { getAiChannelScope, getAiSettings, saveAiSettings } from "./ai/aiSettingsApi";
import { AI_MODEL_CATALOG, normalizeAiCatalog } from "./ai/aiModelCatalog";
import { aiErrorMessage, capabilityLabeler, isGrantable, normalizeCaller } from "./ai/aiErrorMessage";
import AgentsList from "./ai/AgentsList";
import AgentEditor from "./ai/AgentEditor";
import LegacyAssistantForm from "./ai/LegacyAssistantForm";
import ServiceAccountCard from "./ai/ServiceAccountCard";
import HandoffContactsCard from "./ai/HandoffContactsCard";
import AiActionsTab from "./ai/AiActionsTab";
import AiMemoryTab from "./ai/AiMemoryTab";
import AiNoticesTab from "./ai/AiNoticesTab";

// Agentes IA del helpdesk: este courier arma varios asistentes (uno por
// audiencia) además de la configuración base que ya existía. La página es el
// marco — encabezado, interruptor maestro y una sola zona de contenido que
// cambia entre lista, editor de un agente y configuración base — y delega la
// carga/guardado en AgentsList, AgentEditor y LegacyAssistantForm.
//
// La ruta /helpdesk/ai y el ítem del rail los cablea otro cambio (F1); esta
// vista debe existir con este path/export exactos (un test smoke la importa).
//
// SEGURIDAD: ningún agente expone su token. El editor manda api_token solo en el
// body cuando el usuario tipea uno nuevo; acá no se lee ni se loguea.

const PARAM_NEW = "new";
// Sentinela de la configuración base en la URL: ?agent=main abre el formulario
// del asistente del courier (el que existía antes de los agentes).
const PARAM_BASE = "main";

// Pestañas de la página. Sin `tab` en la URL se asume la de agentes (la vista
// histórica), así los links viejos siguen entrando donde mismo.
const TAB_AGENTS = "agentes";
const TAB_ACTIONS = "acciones";
// Memoria aprendida: datos del courier que usan TODOS sus agentes, por eso es
// una pestaña de la página y no una sección del editor de un agente.
const TAB_MEMORY = "memoria";
// Avisos operativos: hechos del courier con vigencia (feriados, cortes de
// servicio) que la IA usa mientras están vigentes; los ven todos sus agentes.
const TAB_NOTICES = "avisos";
// Pestañas donde el editor de un agente no vive: ir a una de ellas limpia el
// ?agent= abierto, y un link con ?agent= y una de ellas se corrige al montar.
const NON_AGENT_TABS = [TAB_ACTIONS, TAB_MEMORY, TAB_NOTICES];

// Copia local del catálogo de agentes: mismo espíritu que aiModelCatalog.js
// para el asistente base. Sin las rutas nuevas (backend viejo) la vista sigue
// operable; las capacidades y audiencias quedan vacías porque son datos que
// solo el backend conoce.
const FALLBACK_CATALOG = {
  providers: AI_MODEL_CATALOG,
  capabilities: [],
  audiences: [],
  channels: [],
  caller: normalizeCaller(),
};

/**
 * Normaliza la respuesta de GET /helpdesk/ai-agents/catalog. Los proveedores
 * pasan por normalizeAiCatalog (misma validación que el catálogo base); el
 * resto de las listas son opcionales y caen a [].
 *
 * `caller` (el rol efectivo de quien mira) también es opcional: sin el campo el
 * front queda con todos los permisos, que es exactamente cómo se comportaba
 * antes de que existiera.
 * @param {object} res
 * @returns {{providers: Array, capabilities: Array, audiences: Array, channels: Array, caller: object}}
 */
function normalizeAgentsCatalog(res) {
  return {
    providers: normalizeAiCatalog(res) || AI_MODEL_CATALOG,
    capabilities: Array.isArray(res?.capabilities) ? res.capabilities : [],
    audiences: Array.isArray(res?.audiences) ? res.audiences : [],
    channels: Array.isArray(res?.channels) ? res.channels : [],
    caller: normalizeCaller(res?.caller),
  };
}

function AgentsPageSkeleton() {
  return (
    <div className="space-y-4" aria-hidden="true">
      <Skeleton className="h-16 rounded-xl" />
      <div className="divide-y divide-border">
        {[0, 1, 2].map((i) => (
          <div key={i} className="py-4 first:pt-0 space-y-2">
            <div className="flex items-center gap-3">
              <Skeleton className="w-10 h-10 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-40 rounded" />
                <Skeleton className="h-3 w-56 rounded" />
              </div>
            </div>
            <Skeleton className="h-11 md:h-8 w-full max-w-xs rounded-md" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AiSettings() {
  const { t } = useTranslation();

  // Qué se está mirando: sin parámetro la lista, ?agent=<id> el editor de ese
  // agente, ?agent=new uno nuevo y ?agent=main la configuración base. En la URL
  // para que back/forward y un link compartido funcionen.
  const [agentParam, setAgentParam] = useUrlState("agent", "");

  // Pestaña activa (?tab=acciones; sin parámetro, agentes). En la URL —y con
  // push, como el resto de los tabs del repo— para que back/forward vuelva a la
  // pestaña anterior en vez de salir de la vista.
  const [tab, setTab] = useUrlState("tab", TAB_AGENTS, { push: true });

  // Audiencia con la que nace un agente nuevo (?audience=internal, el CTA del
  // canal interno). Solo aplica al alta que abrió ese link: el editor la usa
  // como preselección y, al cerrar el alta, se limpia (ver openNew/backToList).
  const [audienceParam, setAudienceParam] = useUrlState("audience", "");

  // Para la limpieza de la URL que sí necesita `replace` (useUrlState con
  // push:true empujaría una entrada de historial por un estado inconsistente).
  const [, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  // Ya hubo una carga completa: distingue la carga inicial (esqueleto de página,
  // sin pestañas) de una recarga pedida por el usuario, que NO puede desmontar
  // las pestañas ni el editor abierto.
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [agentsMissing, setAgentsMissing] = useState(false);

  const [agents, setAgents] = useState([]);
  const [catalog, setCatalog] = useState(FALLBACK_CATALOG);
  // Canales y cuentas del tenant (getAiChannelScope): fuente de los labels del
  // resumen de alcance de cada agente. Sin esa ruta, la lista sigue funcionando.
  const [scope, setScope] = useState([]);
  const [settings, setSettings] = useState(null);
  // Interruptor maestro: undefined = el backend no expuso el campo (switch
  // deshabilitado con nota, sin inventar un valor).
  const [aiEnabled, setAiEnabled] = useState(undefined);
  const [masterSaving, setMasterSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  // Descarta respuestas de una carga vieja (una acción del usuario puede
  // disparar una recarga silenciosa mientras la inicial sigue en vuelo).
  const requestIdRef = useRef(0);
  // StrictMode monta → limpia → monta sobre el MISMO fiber y los refs sobreviven
  // a ese ciclo: hay que re-fijar `true` en el cuerpo del efecto, no solo al
  // declarar el ref (si no, la vista queda cargando para siempre en dev).
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // `t` cambia de identidad cuando cambia el idioma de la interfaz: si entrara a
  // las deps de `load`, traducir la pantalla recargaría la página entera —el
  // editor abierto incluido— y se perdería lo que el usuario estaba editando.
  // El mensaje de error se traduce con la instancia vigente al momento del fallo
  // (ref espejo), no en cada render.
  const tRef = useRef(t);
  tRef.current = t;

  const load = useCallback(
    async ({ silent = false } = {}) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      const isCurrent = () => aliveRef.current && requestIdRef.current === requestId;

      if (!silent) setLoading(true);
      setLoadError(null);

      // En paralelo: la lista manda, pero ninguna de las otras tres puede
      // bloquearla (catálogo, alcance e interruptor maestro son accesorios).
      const [agentsRes, catalogRes, scopeRes, settingsRes] = await Promise.allSettled([
        getAiAgents(),
        getAiAgentsCatalog(),
        getAiChannelScope(),
        getAiSettings(),
      ]);
      if (!isCurrent()) return;

      if (catalogRes.status === "fulfilled") {
        setCatalog(normalizeAgentsCatalog(catalogRes.value));
      }
      setScope(
        scopeRes.status === "fulfilled" && Array.isArray(scopeRes.value?.channels)
          ? scopeRes.value.channels
          : [],
      );

      const loadedSettings =
        settingsRes.status === "fulfilled" ? settingsRes.value?.settings || {} : null;
      setSettings(loadedSettings);

      if (agentsRes.status === "fulfilled") {
        const payload = agentsRes.value || {};
        setAgents(Array.isArray(payload.agents) ? payload.agents : []);
        setAgentsMissing(false);
        // El GET de agentes también expone el flag del tenant: sirve de respaldo
        // cuando el GET de ai-settings no trajo el campo.
        const fromSettings = loadedSettings?.ai_enabled;
        const fromTenant = payload.tenant?.ai_enabled;
        if (typeof fromSettings === "boolean") setAiEnabled(fromSettings);
        else if (typeof fromTenant === "boolean") setAiEnabled(fromTenant);
        else setAiEnabled(undefined);
      } else if (isEndpointMissing(agentsRes.reason)) {
        // Backend sin la ruta de agentes: la vista NO se rompe, cae al
        // formulario de siempre (QA sigue operable con backend viejo).
        setAgents([]);
        setAgentsMissing(true);
        setAiEnabled(
          typeof loadedSettings?.ai_enabled === "boolean" ? loadedSettings.ai_enabled : undefined,
        );
      } else {
        // Un fallo que NO es "ruta ausente" (500, timeout, red) deja la vista en
        // modo error, no en modo "backend viejo": los dos modos son excluyentes
        // y, si no se limpiara el flag, una recarga fallida sobre un 404 previo
        // dejaría la pantalla sin aviso, sin pestañas y sin "Reintentar".
        setAgentsMissing(false);
        setLoadError(agentsRes.reason?.message || tRef.current("No se pudieron cargar los agentes."));
      }

      setLoading(false);
      setLoadedOnce(true);
    },
    [],
  );

  useEffect(() => {
    load();
  }, [load]);

  // Abrir un agente (o la configuración base) implica volver a la pestaña de
  // agentes: el editor vive ahí, no en la de auditoría.
  const openNew = useCallback(() => {
    setTab(TAB_AGENTS);
    setAgentParam(PARAM_NEW);
    // El `?audience=` de la URL era el del link que abrió ESA alta (el CTA del
    // canal interno). Un "Nuevo agente" pedido desde la UI lo descarta: si no,
    // el próximo agente nacería con la audiencia del anterior —y con su alcance
    // y capacidades— sin que nadie la haya elegido.
    setAudienceParam("");
  }, [setAgentParam, setAudienceParam, setTab]);
  const openAgent = useCallback(
    (id) => {
      setTab(TAB_AGENTS);
      setAgentParam(id);
    },
    [setAgentParam, setTab],
  );
  const openBaseConfig = useCallback(() => {
    setTab(TAB_AGENTS);
    setAgentParam(PARAM_BASE);
  }, [setAgentParam, setTab]);
  const backToList = useCallback(() => {
    setAgentParam("");
    // Cerrar el alta cierra también su audiencia preseleccionada (ver openNew).
    setAudienceParam("");
  }, [setAgentParam, setAudienceParam]);

  const handleTabChange = useCallback(
    (next) => {
      setTab(next);
      // El editor no tiene sentido fuera del tab de agentes: con ?agent=<id> en
      // la URL, ir a "Acciones" deja la lista lista para cuando se vuelva.
      if (NON_AGENT_TABS.includes(next) && agentParam) setAgentParam("");
    },
    [agentParam, setAgentParam, setTab],
  );

  // Volver a la lista releyendo: el editor o el formulario base pudieron cambiar
  // algo del courier (modo, proveedor) y de esos datos dependen el interruptor
  // maestro y los defaults del próximo agente. Silencioso: la lista se
  // actualiza sin parpadear a esqueleto.
  const reloadAndBack = useCallback(() => {
    backToList();
    load({ silent: true });
  }, [backToList, load]);
  const handleSaved = reloadAndBack;

  // Acción sobre un agente: bloquea SOLO su tarjeta, avisa por toast y recarga
  // la lista (el backend la reordena por prioridad, así que se relee).
  const runAgentAction = useCallback(
    async (agent, action, messages) => {
      setBusyId(agent.id);
      try {
        await action();
        showToast(t(messages.success), "success");
        await load({ silent: true });
      } catch (err) {
        // Prioridad del texto, de más específico a más genérico:
        // 1. Un `code` tipado (403 de alcance de rol, capacidad no concedible)
        //    se traduce; las capacidades se nombran con las etiquetas del
        //    catálogo, igual que en el editor.
        // 2. El mensaje de NEGOCIO del backend (409 "Ya alcanzaste el máximo de
        //    20 agentes", 400 "Activa el agente después de configurar su
        //    proveedor", 404 "Agente no encontrado"): explica por qué falló
        //    mejor que el genérico de la acción. Un 5xx queda fuera: su texto es
        //    interno y no se le muestra al usuario.
        // 3. El mensaje de la acción, que dice QUÉ se intentaba hacer.
        const typedMessage = err?.code ? aiErrorMessage(err, t, capabilityLabeler(catalog)) : "";
        const status = apiErrorStatus(err);
        const serverMessage = status !== null && status < 500 ? apiErrorMessage(err, "") : "";
        // Un 404 CON cuerpo del backend no es una ruta ausente: el agente ya no
        // existe (lo borró otro usuario y esta pestaña quedó vieja). Se recarga
        // en silencio para que desaparezca de la lista; `load` maneja sus
        // propios errores (allSettled), no hace falta envolverla.
        if (status === 404 && serverMessage) load({ silent: true });
        showToast(
          // Solo un 404/405 SIN texto útil es la ruta realmente sin desplegar.
          isEndpointMissing(err) && !serverMessage
            ? t("Los agentes están en construcción.")
            : typedMessage || serverMessage || t(messages.failure),
          "error",
        );
      } finally {
        setBusyId(null);
      }
    },
    [catalog, load, t],
  );

  const handleDuplicate = useCallback(
    (id) => {
      const agent = agents.find((a) => a.id === id);
      if (!agent) return;
      runAgentAction(agent, () => duplicateAiAgent(id), {
        success: "Agente duplicado.",
        failure: "No se pudo duplicar el agente.",
      });
    },
    [agents, runAgentAction],
  );

  // Pausar/activar es un PUT parcial: solo viaja el estado.
  const handleToggleStatus = useCallback(
    (agent) => {
      const nextStatus = agent.status === "active" ? "paused" : "active";
      runAgentAction(agent, () => updateAiAgent(agent.id, { status: nextStatus }), {
        success: nextStatus === "paused" ? "Agente pausado." : "Agente activado.",
        failure: "No se pudo cambiar el estado del agente.",
      });
    },
    [runAgentAction],
  );

  const handleDelete = useCallback(
    (agent) => {
      runAgentAction(agent, () => deleteAiAgent(agent.id), {
        success: "Agente eliminado.",
        failure: "No se pudo eliminar el agente.",
      });
    },
    [runAgentAction],
  );

  // Interruptor maestro del courier. El `mode` se RELEE justo antes de guardar:
  // el formulario base cambia el modo por su cuenta y mandar el que quedó
  // cacheado al montar la página volvería a encender el asistente que el usuario
  // acaba de apagar. Si esa lectura falla, no se guarda nada.
  // La configuración base se guarda desde LegacyAssistantForm: su respuesta
  // refresca lo que esta página deriva de ella (interruptor maestro y bloqueo
  // por acceso a pedidos) sin esperar a volver a la lista.
  const handleBaseSaved = useCallback((saved) => {
    if (!saved || typeof saved !== "object") return;
    setSettings((prev) => ({ ...(prev || {}), ...saved }));
    if (typeof saved.ai_enabled === "boolean") setAiEnabled(saved.ai_enabled);
  }, []);

  const handleMasterToggle = useCallback(
    async (next) => {
      if (masterSaving) return;
      const previous = aiEnabled;
      setMasterSaving(true);
      setAiEnabled(next);
      try {
        // Sin la lectura fresca no se guarda: el `mode` cacheado al montar puede
        // ser el que el formulario base ya cambió, y mandarlo volvería a
        // encender el asistente que el usuario acaba de apagar. Mejor abortar.
        // Si la respuesta fresca no trae `mode`, tampoco se inventa uno: el PUT
        // va sin esa clave (parcial) en vez de mandar el cacheado.
        const fresh = await getAiSettings();
        const res = await saveAiSettings({ mode: fresh?.settings?.mode, ai_enabled: next });
        const saved = res?.settings || {};
        setAiEnabled(typeof saved.ai_enabled === "boolean" ? saved.ai_enabled : next);
        setSettings((prev) => ({ ...(prev || {}), ...(fresh?.settings || {}), ...saved }));
        showToast(
          next ? t("IA activada para este courier.") : t("IA desactivada: ningún agente responderá."),
          next ? "success" : "warning",
        );
      } catch (err) {
        setAiEnabled(previous);
        // Solo un error TIPADO (el backend dice qué permiso falta) reemplaza al
        // mensaje de la pantalla: un fallo suelto no aporta nada más útil que el
        // genérico, y su `message` es texto de servidor sin traducir.
        const typedMessage = err?.code ? aiErrorMessage(err, t) : "";
        showToast(
          isEndpointMissing(err)
            ? t("El interruptor de IA todavía no está disponible.")
            : typedMessage || t("No se pudo cambiar el interruptor de IA."),
          "error",
        );
      } finally {
        setMasterSaving(false);
      }
    },
    [aiEnabled, masterSaving, t],
  );

  // Un link viejo a ?agent=<id>&tab=acciones (el editor vive en la pestaña de
  // agentes) deja el `tab` mintiendo sobre lo que se ve. Se limpia al montar con
  // replace: el historial no gana una entrada por un estado inconsistente al que
  // "atrás" no debería volver.
  useEffect(() => {
    if (!agentParam || !NON_AGENT_TABS.includes(tab)) return;
    const params = new URLSearchParams(window.location.search);
    params.delete("tab");
    setSearchParams(params, { replace: true });
  }, [agentParam, tab, setSearchParams]);

  // El editor recibe la config del courier: le sirve para mostrar de dónde
  // hereda un agente y para saber si la IA está encendida.
  const tenantSettings = useMemo(
    () => ({ ...(settings || {}), ai_enabled: aiEnabled }),
    [settings, aiEnabled],
  );

  // Pestaña efectiva: un ?agent= abierto manda (el editor vive en la de
  // agentes) y un ?tab= desconocido cae a la de agentes en vez de dejar el
  // panel en blanco.
  const activeTab = agentParam ? TAB_AGENTS : NON_AGENT_TABS.includes(tab) ? tab : TAB_AGENTS;

  // Solo la PRIMERA carga pinta el esqueleto de página y esconde las pestañas:
  // una recarga (reintentar, volver de guardar) actualiza en su lugar y no
  // desmonta el editor abierto.
  const booting = loading && !loadedOnce;

  const masterAvailable = typeof aiEnabled === "boolean";
  // El rol del llamador viaja con el catálogo; sin ese dato queda todo permitido
  // (comportamiento idéntico al de antes de que existiera `caller`).
  const permissions = catalog.caller;
  const canConfigure = permissions.can_configure;
  // La config del courier lee pedidos y este rol no puede conceder ese acceso:
  // el backend solo le acepta APAGAR la IA. El interruptor maestro no ofrece
  // encenderla (sería un 403 garantizado con el control por rol activo).
  const canGrantOrders = isGrantable(
    (Array.isArray(catalog.capabilities) ? catalog.capabilities : []).find(
      (c) => c?.id === "estado_pedido",
    ),
  );
  const masterOnlyOff =
    !canGrantOrders && settings?.data_access_orders !== false && aiEnabled !== true;
  // Avisos de derivación: el backend los trata como config de la IA, así que
  // siguen las mismas reglas de rol que el resto (sin permiso de configurar, o
  // con la IA leyendo pedidos que el rol no puede conceder, no se editan).
  const handoffReadOnlyReason = !canConfigure
    ? "role"
    : !canGrantOrders && settings?.data_access_orders !== false
      ? "orders"
      : null;
  const editingAgentId = agentParam && agentParam !== PARAM_BASE ? agentParam : null;
  const showChrome = !booting && !agentsMissing && !loadError;

  return (
    // Sin activeKey: el rail deriva el ítem activo del pathname (/helpdesk/ai).
    <HelpdeskSuiteLayout>
      <div className="flex flex-col h-full min-h-0 bg-bg">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-5 py-5 space-y-5">
            <PageHeader
              icon={
                <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0">
                  <Sparkles className="w-5 h-5" aria-hidden="true" />
                </span>
              }
              title={t("Asistente IA")}
              subtitle={t("Configura agentes por audiencia, o deja que el agente principal atienda todo el courier.")}
              actions={
                showChrome && canConfigure ? (
                  <Button
                    onClick={openNew}
                    iconLeft={<Plus className="w-4 h-4" aria-hidden="true" />}
                  >
                    {t("Nuevo agente")}
                  </Button>
                ) : undefined
              }
            />

            {showChrome && (
              <Card padding="md">
                <div className="flex items-start justify-between gap-4">
                  <span className="min-w-0">
                    <span className="block text-xs font-medium text-fg">
                      {t("IA activa para este courier")}
                    </span>
                    <span id="ai-master-help" className="block text-[12px] md:text-[11px] text-fg-muted">
                      {t("Con la IA apagada, ningún agente responde: los mensajes quedan para tu equipo.")}
                    </span>
                  </span>
                  <Switch
                    id="ai-master"
                    size="md"
                    // `=== true`: sin dato el switch se pinta apagado, no
                    // encendido — mostrarlo prendido afirmaría algo que el
                    // backend no dijo.
                    checked={aiEnabled === true}
                    disabled={!masterAvailable || masterSaving || !canConfigure || masterOnlyOff}
                    onChange={handleMasterToggle}
                    ariaLabel={t("IA activa para este courier")}
                    aria-describedby={
                      !canConfigure || masterOnlyOff ? "ai-master-help ai-master-role" : "ai-master-help"
                    }
                  />
                </div>
                {!masterAvailable && (
                  <p className="mt-2 text-[12px] md:text-[11px] text-fg-muted">
                    {t("Esta opción está en construcción.")}
                  </p>
                )}
                {(!canConfigure || masterOnlyOff) && (
                  <p id="ai-master-role" className="mt-2 text-[12px] md:text-[11px] text-fg-muted">
                    {canConfigure
                      ? t("La IA de la empresa lee pedidos y tu rol no puede conceder ese acceso: solo puedes apagarla.")
                      : t("Tu rol no permite esta acción de la IA.")}
                  </p>
                )}
              </Card>
            )}

            {booting && <AgentsPageSkeleton />}

            {!booting && !agentsMissing && loadError && (
              <Card padding="lg" className="max-w-md mx-auto text-center">
                <div className="flex items-center justify-center gap-1.5 text-xs text-status-error">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                  {loadError}
                </div>
                <button
                  type="button"
                  onClick={() => load()}
                  className="mt-2 h-8 px-3 rounded-md text-xs font-medium text-brand-700 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
                >
                  {t("Reintentar")}
                </button>
              </Card>
            )}

            {/* Las pestañas son el marco de la página, no un accesorio de la
                lista: con backend viejo (sin GET /helpdesk/ai-agents) siguen
                arriba, porque "Acciones" y la cuenta de servicio tienen sus
                propias rutas y deben seguir siendo alcanzables. */}
            {!booting && !loadError && (
              <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList ariaLabel={t("Secciones del asistente IA")}>
                  <TabsTrigger
                    value={TAB_AGENTS}
                    icon={<Bot className="w-3.5 h-3.5" aria-hidden="true" />}
                  >
                    {t("Agentes")}
                  </TabsTrigger>
                  <TabsTrigger
                    value={TAB_ACTIONS}
                    icon={<Zap className="w-3.5 h-3.5" aria-hidden="true" />}
                  >
                    {t("Acciones")}
                  </TabsTrigger>
                  <TabsTrigger
                    value={TAB_MEMORY}
                    icon={<Brain className="w-3.5 h-3.5" aria-hidden="true" />}
                  >
                    {t("Memoria aprendida")}
                  </TabsTrigger>
                  <TabsTrigger
                    value={TAB_NOTICES}
                    icon={<Megaphone className="w-3.5 h-3.5" aria-hidden="true" />}
                  >
                    {t("Avisos operativos")}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value={TAB_AGENTS} className="pt-4 space-y-4">
                  {/* Con qué usuario de Beartrack ejecuta la IA las acciones.
                      Va acá y no en el chrome de la página: es configuración
                      de los agentes, no un ajuste global del courier. */}
                  <ServiceAccountCard caller={permissions} />
                  {/* A quién se le avisa cuando la IA deriva un caso: vale para
                      todo el courier, no para un agente, así que no se muestra
                      dentro del editor de uno. */}
                  {(agentsMissing || !editingAgentId) && (
                    <HandoffContactsCard readOnlyReason={handoffReadOnlyReason} />
                  )}
                  {agentsMissing ? (
                    // Backend sin la ruta de agentes: aviso + formulario de
                    // siempre, dentro de la pestaña (el editor no tiene de dónde
                    // leer un agente, así que un ?agent= abierto cae acá).
                    <div className="space-y-4">
                      <PendingBackendNotice>
                        {t(
                          "Los agentes por audiencia están en construcción. Puedes seguir configurando el asistente del courier.",
                        )}
                      </PendingBackendNotice>
                      <LegacyAssistantForm
                        caller={permissions}
                        capabilities={catalog.capabilities}
                        onSaved={handleBaseSaved}
                      />
                    </div>
                  ) : agentParam === PARAM_BASE ? (
                    <div className="space-y-4">
                      <Button
                        variant="ghost"
                        // h-10 (no h-8): en móvil es el mismo alto táctil que el
                        // botón de la cabecera.
                        size="md"
                        onClick={reloadAndBack}
                        iconLeft={<ArrowLeft className="w-4 h-4" aria-hidden="true" />}
                      >
                        {t("Volver a la lista")}
                      </Button>
                      {/* El rol del llamador también acota la configuración
                          base: proveedor y credencial son de admin, y el
                          acceso a pedidos depende de las capacidades que el
                          rol puede conceder. */}
                      <LegacyAssistantForm
                        caller={permissions}
                        capabilities={catalog.capabilities}
                        onSaved={handleBaseSaved}
                      />
                    </div>
                  ) : editingAgentId ? (
                    <AgentEditor
                      // key: cambiar de agente (o de "nuevo" a editar) remonta el
                      // editor en vez de reusar el estado del anterior.
                      key={editingAgentId}
                      // El editor distingue alta de edición por el literal "new"
                      // (su propio contrato): el id viaja tal cual salvo eso.
                      agentId={editingAgentId}
                      catalog={catalog}
                      scope={scope}
                      tenantSettings={tenantSettings}
                      // El listado respondió: un 404 de ESTE agente es "ya no
                      // existe", no "la ruta está sin desplegar".
                      agentsAvailable={!agentsMissing}
                      initialAudience={audienceParam || undefined}
                      onSaved={handleSaved}
                      onCancel={backToList}
                    />
                  ) : (
                    <AgentsList
                      agents={agents}
                      catalog={catalog}
                      scope={scope}
                      loading={loading}
                      busyId={busyId}
                      onNew={openNew}
                      onEdit={openAgent}
                      onDuplicate={handleDuplicate}
                      onToggleStatus={handleToggleStatus}
                      onDelete={handleDelete}
                      onOpenLegacy={openBaseConfig}
                    />
                  )}
                </TabsContent>

                <TabsContent value={TAB_ACTIONS} className="pt-4">
                  <AiActionsTab agents={agents} catalog={catalog} />
                </TabsContent>

                <TabsContent value={TAB_MEMORY} className="pt-4">
                  {/* Leer la memoria es de cualquier rol con acceso a la IA;
                      cambiarla es admin_only en el backend, que es el mismo
                      tier que expone can_manage_service_account. El backend
                      revalida cada escritura: ocultar botones no es seguridad. */}
                  <AiMemoryTab
                    catalog={catalog}
                    canManage={permissions.can_manage_service_account}
                  />
                </TabsContent>

                <TabsContent value={TAB_NOTICES} className="pt-4">
                  {/* Leer los avisos es de cualquier rol con acceso a la IA;
                      escribirlos es `configure` en el backend (can_configure),
                      y un aviso "sin operación" para todas las audiencias es
                      admin_only (can_manage_service_account). Ocultar botones
                      no es seguridad: el backend revalida cada escritura. */}
                  <AiNoticesTab
                    canManage={canConfigure}
                    canManageAdminOnly={permissions.can_manage_service_account}
                  />
                </TabsContent>
              </Tabs>
            )}
          </div>
        </div>
      </div>
    </HelpdeskSuiteLayout>
  );
}
